import { useCallback } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { checkVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useBreakpoint } from '@/hooks/useBreakpoint'

import { BuilderPageCore } from '../../__project__/BuilderPageCore'
import { BuilderComponent } from './BuilderComponent'
import { VisibilityOverlay } from './VisibilityOverlay'

// todo: handle dnd in preview mode
const canDnd = true

/**
 * Builder Page
 *
 * @mode preview (builder) only
 */
export const RenderPage = () => {
  // Get component list
  const {
    activePage,
    activePageClient,
    pages,
    activePageIndex,
    goToPageSafely,
    previewedPageIds,
    setPreviewedPageIds,
  } = useAppPagesContext()
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { userEmail } = useAppAuthContext()
  const { device } = useBreakpoint()

  const checkPageVisibility = useCallback(
    (page: any) => {
      return page.visibility
        ? checkVisibilityV2(
            page.visibility,
            dbClient,
            userEmail,
            language,
            userSpecific,
            undefined, // tableId
            undefined, // rowIndex
            undefined, // formState
            device
          )
        : true
    },
    [dbClient, userEmail, language, userSpecific, device]
  )

  const getNextVisiblePage = useCallback(() => {
    if (!pages || pages.length === 0) return null

    // Start from the next page after current active page
    for (let i = activePageIndex + 1; i < pages.length; i++) {
      const page = pages[i]
      if (checkPageVisibility(page)) return page
    }

    // If no visible page found after current, check from the beginning
    for (let i = 0; i < activePageIndex; i++) {
      const page = pages[i]
      if (checkPageVisibility(page)) return page
    }

    return null
  }, [pages, activePageIndex, checkPageVisibility])

  // // check if current page has FAB
  // const alreadyHasFab = !!activePage?.elements.some((el) => el?.compId === 'fab')
  // const printAPIError = useReqError()

  // const insertCard = useCallback(
  //   (index: number, { settings, ...item }: BuilderDraggableElement) => {
  //     let newChildEl: BuilderDroppableElement | undefined
  //     let newChildElSetting: ElementSetting | undefined

  //     if (item.compId === 'form') {
  //       const btnCompGroup = DEFAULT_COMPONENTS_GROUPS.find((g) => g.id === 'button')
  //       const btnComp = btnCompGroup?.components.find((c) => c.compId === 'button')
  //       const childId = uuid4()
  //       if (btnCompGroup && btnComp) {
  //         newChildEl = {
  //           id: childId,
  //           compId: btnComp.compId,
  //           groupId: btnCompGroup.id,
  //           title: btnComp.title,
  //           icon: btnComp.icon,
  //           inactive: false,
  //           nested: btnComp.nested,
  //           isForm: btnComp.isForm,
  //           isDetailPageComponent: btnComp.isDetailPageComponent,
  //           children: [],
  //           parentId: item.id,
  //           parentTitle: item.title,
  //           parentType: item.compId,
  //           removable: false,
  //           draggable: false,
  //           isSubmit: true,
  //         }
  //         //   newChildElSetting = parseCompSettings(
  //         //     btnComp,
  //         //     {
  //         //       menus,
  //         //       tables: dbTables,
  //         //       flattened: [],
  //         //     },
  //         //     activePage?.tableId
  //         //   )
  //       }
  //     }

  //     try {
  //       // updateContainerValue((prev) => {
  //       //   prev.elements.splice(index, 0, {
  //       //     ...item,
  //       //     children: newChildEl ? [newChildEl] : [],
  //       //     inactive: false,
  //       //   })
  //       //   if (newChildElSetting && newChildEl) {
  //       //     prev.elementSettings[newChildEl.id] = newChildElSetting
  //       //   }
  //       //   prev.elementSettings[item.id] = settings
  //       // }).catch(printAPIError)
  //     } catch (e) {
  //       printAPIError(e as ErrorResponse)
  //     }
  //     //.catch(printAPIError)
  //   },
  //   [printAPIError]
  // )

  // // Drag methods from component into page
  // const [{ isOver, canDrop }, drop] = useDrop(
  //   () => ({
  //     accept: 'component',
  //     hover: (item: BuilderDraggableElement, monitor) => {
  //       if (!monitor.canDrop()) {
  //         return
  //       }

  //       // const isFAB = item.compId === 'fab'

  //       // // prevent adding FAB in overlays
  //       // if (activeContainer && isFAB) return

  //       // // prevent adding FAB if its already exist in current page
  //       // if (isFAB) {
  //       //   // show warning modal
  //       //   if (alreadyHasFab) {
  //       //     setShowFabWarningModal(true)
  //       //     return
  //       //   }
  //       // }

  //       // if (item.index === -1) {
  //       //   const itemIndex = activePage?.elements.length

  //       //   if (typeof itemIndex !== 'undefined') {
  //       //     insertCard(itemIndex, item)
  //       //     item.index = itemIndex
  //       //   }
  //       // }
  //     },
  //     drop: (item) => {
  //       if (alreadyHasFab) return
  //       // setActiveComponent({
  //       //   compId: item.compId,
  //       //   id: item.id,
  //       //   groupId: item.groupId,
  //       // })
  //     },
  //     collect: (monitor) => {
  //       return {
  //         name: 'component-droppable',
  //         isOver: monitor.isOver(),
  //         canDrop: monitor.canDrop(),
  //         isOverCurrent: monitor.isOver({ shallow: true }),
  //       }
  //     },
  //   }),
  //   [activePage?.elements.length, insertCard, alreadyHasFab]
  // )

  if (!activePage || !activePageClient) return null

  const handlePreviewAnyway = () => {
    if (activePage?.id && !previewedPageIds.includes(activePage.id)) {
      setPreviewedPageIds((prev) => [...prev, activePage.id])
    }
  }

  const handleHideScreen = () => {
    if (activePage?.id) {
      setPreviewedPageIds((prev) => prev.filter((id) => id !== activePage.id))
    }
    const nextVisiblePage = getNextVisiblePage()
    if (nextVisiblePage) {
      goToPageSafely(nextVisiblePage.slug || activePage.slug || '')
    }
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <BuilderPageCore
        key={activePage.id}
        // ref={canDnd ? drop : undefined}
        page={activePage}
        pageClient={activePageClient}
      >
        {(props) => {
          return previewedPageIds.includes(activePage?.id || '') || checkPageVisibility(activePage) ? (
            <BuilderComponent {...props} />
          ) : (
            <VisibilityOverlay onPreviewAnyway={handlePreviewAnyway} onHideScreen={handleHideScreen} />
          )
        }}
      </BuilderPageCore>
    </DndProvider>
  )
}
